/**
 * Fixtures for the careOS prototype surface (patient profile + labs).
 *
 * Read-only seed data: the lab test catalog, suggested panels, a patient's
 * prior lab history, and the patient profile. Served via GET handlers
 * (`handlers/careOSPrototype.ts`) and read through react-query hooks, mirroring
 * how production would fetch them. The interactive labs state (placing orders,
 * uploading result attachments, mark-as-read) is client-side prototype state —
 * it has no backend yet, so it is NOT modeled here.
 *
 * No `db` slice (unlike `providerHome`): nothing mutates this data
 * server-side, so handlers read these fixtures directly.
 *
 * Dates are hardcoded display strings (the prototype's labs UI renders them
 * as-is); they are intentionally not anchored to "now".
 */
import type {
  LabEntry,
  OrderAttachment,
  FollowUp,
  ClaimsInsightsState,
  ClaimsInsightsData,
  ClaimsInsight,
  InsightType,
  InsightSeverity,
  PatientSummaryDetail,
} from '../../models/careOSPrototype';

// ─── Lab catalog ────────────────────────────────────────────────────────────

// Suggested multi-test panels surfaced in the order composer.
export const labPanels = [
  { id: 'gi', label: 'GI Panel', tests: ['CBC', 'CMP', 'Vitamin D', 'Iron', 'Calprotectin'] },
  { id: 'iron', label: 'Iron Panel', tests: ['Iron', 'Ferritin', 'TIBC'] },
  { id: 'metabolic', label: 'Metabolic Panel', tests: ['CMP', 'HbA1c', 'Lipid Panel'] },
];

// Placeholder lab test catalog — production replaces this with the Healthie API.
// Ordered loosely by clinical category so the order dropdown reads naturally.
export const labTestCatalog: string[] = [
  // Hematology
  'CBC',
  'CBC with Differential',
  'Reticulocyte Count',
  // Comprehensive panels
  'CMP',
  'BMP',
  'Lipid Panel',
  // GI markers
  'Calprotectin',
  'H. pylori',
  'Stool Culture',
  'GI Pathogen Panel',
  'Celiac Panel',
  'Lactose Breath Test',
  'Hydrogen Breath Test',
  'Lipase',
  'Amylase',
  // Liver
  'AST',
  'ALT',
  'Bilirubin',
  'Albumin',
  // Iron studies
  'Iron',
  'Ferritin',
  'TIBC',
  'Transferrin Saturation',
  // Thyroid
  'TSH',
  'Free T3',
  'Free T4',
  // Vitamins / minerals
  'Vitamin D',
  'Vitamin B12',
  'Folate',
  'Vitamin A',
  'Magnesium',
  'Phosphorus',
  'Zinc',
  'Selenium',
  // Inflammation / autoimmune
  'CRP',
  'ESR',
  'ANA',
  'HbA1c',
];

// ─── Lab history ────────────────────────────────────────────────────────────
// The prototype demos a single patient's prior results; the same history is
// served for any patientId until per-patient history exists.
export const initialLabHistory: LabEntry[] = [
  { id: 'l1', date: 'Mar 12, 2026', status: 'completed', tests: ['CBC', 'CMP', 'Vitamin D'], vendor: 'Labcorp', deliveryMethod: 'auto-received' },
  { id: 'l2', date: 'Dec 4, 2025', status: 'out-of-range', tests: ['Calprotectin', 'Iron'], vendor: 'Quest', deliveryMethod: 'auto-received' },
  { id: 'lu1', date: 'Nov 1, 2025', status: 'completed', tests: ['Stool Culture', 'H. pylori'], vendor: 'Patient copy', deliveryMethod: 'uploaded', fileName: 'kelso_stool_oct25.pdf' },
  { id: 'l3', date: 'Sep 18, 2025', status: 'completed', tests: ['GI Panel'], vendor: 'Labcorp', deliveryMethod: 'auto-received' },
];

// ─── Prototype-bar lab scenarios ────────────────────────────────────────────
// Demo data behind the Stage dropdown's 'review' and 'done' scenarios (see
// `JourneyStage`). These aren't served over the mock API — the prototype bar
// reads them directly, the same way ClaimsInsightsPanel reads
// `claimsInsightsFixtures`. They live here rather than in the bar so the demo
// shapes stay with the rest of the prototype's fixture data.

// A checked-in PDF rather than a `createObjectURL` blob: nothing was uploaded
// in this session, so there's no File to make a blob from. It's a real URL the
// drawer's <iframe> can load, so the PDF viewer, slideshow, and page nav all
// work instead of falling through to "PDF preview unavailable".
const DEMO_RESULT_PDF = `${process.env.PUBLIC_URL ?? ''}/demo/lab-result-cbc-cmp.pdf`;

/**
 * Results back from the lab on the synthetic active order — what makes the
 * 'review' stage the PRD's actual review moment rather than just "an order
 * exists". With ≥1 attachment the card shows the Partial Complete chip, Mark as
 * Reviewed leaves its disabled state, and the drawer opens on the PDF viewer
 * instead of the empty-state upload form (PRD, Feature 3).
 *
 * `range` is deliberately unset on both. The range read is the work the
 * provider is here to do; pre-tagging it hands them the conclusion. It also
 * keeps the scenario honest about the PRD contract that Source/Range never gate
 * Mark as Reviewed — the button is live with both dropdowns on "Not set".
 */
export const demoResultAttachments: OrderAttachment[] = [
  {
    id: 'demo-att-1',
    fileName: 'Kelso_CBC_Apr2026.pdf',
    source: 'provider-sent',
    uploadedAt: 'Apr 24, 2026',
    fileUrl: DEMO_RESULT_PDF,
  },
  {
    id: 'demo-att-2',
    fileName: 'Kelso_CMP_Apr2026.pdf',
    source: 'provider-sent',
    uploadedAt: 'Apr 24, 2026',
    fileUrl: DEMO_RESULT_PDF,
  },
];

/**
 * The 'review' order after it was reviewed — the tile the 'done' stage prepends
 * to the gallery, taking it from 4 fixtures to 5. Shaped the way
 * `handleMarkAsRead` shapes a terminalized order (tests retained, vendor from
 * the attachment source, `deliveryMethod: 'uploaded'`) so it reads identically
 * to one a presenter terminalized by hand.
 *
 * Status 'completed' → the "In Range" pill (PRD Feature 5: the display label
 * changed, the underlying value didn't). Out of Range stays reachable via the
 * Labs dropdown.
 *
 * The attachments don't carry over: `LabEntry` has nowhere to put them. PRD
 * Feature 5 calls for it ("Entries created via Mark as Reviewed carry their
 * full attachments array") but the type and drawer don't implement it yet, so
 * this tile's drawer shows the single synthesized attachment every gallery tile
 * shows today.
 */
export const reviewedDemoResult: LabEntry = {
  id: 'demo-reviewed',
  date: 'Apr 22, 2026',
  status: 'completed',
  tests: ['CBC w/ Differential', 'Comprehensive Metabolic Panel'],
  vendor: 'External provider',
  deliveryMethod: 'uploaded',
  fileName: 'Kelso_CMP_Apr2026.pdf',
};

// ─── Patient profile ──────────────────────────────────────────────────────────
// Keyed by patient id. Rich demo shape consumed by UserDashboardPage / the
// patient sidebar. Typed by inference (the prototype never declared a formal
// interface); the consuming hook in Prompt 5 derives its type from this export.
export const patientProfiles = {
  p1: {
    name: 'Nina Kelso',
    age: 34,
    sex: 'F',
    ethnicity: 'Caucasian',
    dob: '11/27/91',
    memberSince: 'Jan 2024',
    membershipType: 'Collab',
    healthieId: '123456',
    // Patient Summary panel: the member's chief complaint, leading with the
    // diagnosis. Name- and pronoun-free, since other patients borrow this body
    // in the mock handler; only what we have is included.
    //
    // After the first visit (Steady State) it's written from the visit
    // transcript: what the member presents with, the background, what's
    // happened since, and what they're seeking.
    chiefComplaint:
      'Diagnosed with IBS-C and GERD, with bloating, diarrhea, and reflux as the primary symptoms and a history of prior GI surgery; referred by Dr. Montgomery (Mayo Clinic). Frequent bathroom trips disrupt a workday spent on calls, and since a medication change in November 2025, reports stomach aches and difficulty getting out of bed. Seeks to reduce bathroom frequency and manage medication side effects.',
    // Before the first visit (Summary + Claims) there's no transcript yet, so
    // it's written from three onboarding answers: "What symptoms can we help
    // you manage?" (bloating, diarrhea, heartburn), "Have you been diagnosed
    // with a GI condition?" (IBS - Constipation, GERD/reflux), and "Which best
    // describes where you're at right now?" ("I've tried a lot already, and
    // most of it hasn't worked").
    onboardingChiefComplaint:
      'Diagnosed with IBS-C and GERD, with bloating, diarrhea, and heartburn as the primary symptoms. Has tried a lot already, and most of it hasn\'t worked.',
    // Patient Summary drawer (opened from the steady-state summary card).
    // Fictional, written to agree with the rest of this profile — the Feb 23
    // dietitian and coach visits, the Nov 13 follow-up, the Apr 22 labs, the
    // care blocks, and the Pharmacy tab's meds (nortriptyline is the Nov 13
    // medication change). Pronoun-free, like the chief complaint, since other
    // patients borrow this body in the mock handler.
    summaryDetail: {
      highlights: [
        'Diet work is paying off: reports fewer urgent bathroom trips during work calls since cutting dairy and garlic, 2 of the 4 suspected trigger foods.',
        'Nortriptyline 25 mg (started Nov 13) is causing morning grogginess and stomach aches — difficulty getting out of bed most mornings; asking for help managing side effects.',
        'Labs completed Apr 22 (CBC, CMP): 2 values out of range. The repeat order to Labcorp failed to transmit, so follow-up labs haven\'t been drawn.',
      ],
      progress: {
        working: [
          'Elimination Challenge: fewer urgent bathroom trips since cutting dairy and garlic',
          'Mind-Gut Masterclass: uses the breathing exercise before long work calls',
          'Proactive communication: messages the coach with detailed updates on symptoms and side effects',
        ],
        challenging: [
          'Medication side effects: morning grogginess and stomach aches since starting nortriptyline',
          'Hydration: hard to keep up Stay Hydrated on call-heavy days; streak ended 8 days ago',
        ],
      },
      sentiment: {
        quote: 'Cutting dairy has honestly been a game changer — I made it through a two-hour call last week without running out once! But the new med is rough. Most mornings I can barely get out of bed.',
        source: 'Message to coach',
        date: 'Oct 20, 2026',
        read: 'This reflects a hopeful-but-worn-down tone — encouraged by real progress on the diet, but more frustrated with medication side effects than in the February check-ins.',
      },
      meetings: [
        {
          id: 'mtg-2026-02-23-coach',
          date: 'Feb 23, 2026',
          visitType: 'Coaching session',
          provider: 'E. Rayas, Coach',
          planStyle: 'goals',
          plan: [
            { label: 'Build this Mind-Gut Skill', text: 'Do one Mind-Gut Masterclass module a week, and use the breathing exercise before my longest work call each day.' },
            { label: 'Stay Hydrated', text: 'Keep a water bottle at my desk and log my water intake in the app every day.' },
            { label: 'Build Awareness', text: 'Notice which days my symptoms are calmer, and what I ate or did differently.' },
          ],
        },
        {
          id: 'mtg-2026-02-23-rd',
          date: 'Feb 23, 2026',
          visitType: 'Nutrition visit',
          provider: 'J. Brown, RD',
          planStyle: 'recommendations',
          plan: [
            { label: 'Nutrition', text: 'Start the Elimination Challenge — remove the 4 suspected trigger foods (dairy, garlic, onion, wheat) and log 21 Gut Checks. Pause Low-FODMAP Reintroduction until the challenge is done.' },
            { label: 'Referrals', text: 'Continue with the health coach (E. Rayas) for habit and hydration support.' },
          ],
        },
        {
          id: 'mtg-2025-11-13-md',
          date: 'Nov 13, 2025',
          visitType: 'Follow-up visit',
          provider: 'T. Haywood, MD',
          planStyle: 'recommendations',
          plan: [
            { label: 'Nutrition', text: 'Continue current diet and daily food journal until the dietitian visit.' },
            { label: 'Mind-gut', text: 'Mind-Gut Masterclass assigned in the Ayble app.' },
            { label: 'Ordered labs', text: 'Share previous lab work; new CBC and CMP ordered.' },
            { label: 'Medications', text: 'Start nortriptyline 25 mg nightly for IBS symptoms. Continue lansoprazole 30 mg daily for GERD.' },
            { label: 'Referrals', text: 'Ayble dietitian (J. Brown, RD) for trigger-food work; health coach (E. Rayas) for mind-gut and habit support.' },
          ],
        },
        {
          id: 'mtg-2025-04-15-md',
          date: 'Apr 15, 2025',
          visitType: 'Initial consultation',
          provider: 'T. Haywood, MD',
          planStyle: 'recommendations',
          plan: [
            { label: 'Nutrition', text: 'Start a daily food and symptom journal.' },
            { label: 'Ordered labs', text: 'Complete new lab work (CBC, CMP).' },
            { label: 'Medications', text: 'Continue lansoprazole 30 mg daily. Start IBgard before meals.' },
            { label: 'Referrals', text: 'Complete Gut Check app onboarding and the baseline symptom survey.' },
          ],
        },
      ],
    } as PatientSummaryDetail,
    // Care Continuity tab: the member's own words on what they most want to
    // achieve with Ayble — uncovered during the first visit, then carried
    // forward so every provider sees the same goal (hence "continuity").
    careGoal:
      "I want to be going to the bathroom less, because I'm on calls all day for work. Being interrupted every hour makes me less productive.",
    // Where the goal was captured — stamped under it so later providers know
    // which visit it came from and how old it is.
    careGoalSource: { visit: 'Initial Consultation', date: 'Apr 15, 2025' },
    // Care Block Detail entry the goal card opens — same drawer and feedback
    // form as the Care Guide blocks. Seeded in `fixtures/careBlockDetail.ts`.
    careGoalBlockId: 'uncovered-goal',
    billingPriorities: ['99213', 'G2211', 'G0447'],
    recentCharts: [
      { author: 'T. Haywood, MD', date: 'Nov 13' },
      { author: 'J. Brown, RD', date: 'Feb 23' },
      { author: 'E. Rayas, Coach', date: 'Feb 23' },
    ],
    symptomChange: { value: '51%', baseline: '42%', trend: 'up' as const },
    gutChecks: { value: '8', lastSurvey: 'Feb 23', appStatus: 'baseline-complete' as const },
    teamTrustScore: { value: '76%', memberAverage: '68%' },
    requestedFollowUps: [
      { provider: 'T. Haywood, MD', status: 'Confirmed', date: '4/15/2025', time: '8:45 AM EST' },
      { provider: 'E. Rayas, Coach', status: 'Unconfirmed', date: '4/15/2025', time: '8:45 AM EST' },
      { provider: 'J. Brown, RD', status: 'Unconfirmed', date: '4/15/2025', time: '8:45 AM EST' },
      { provider: 'J. Brown, RD', status: 'Unscheduled', date: 'Nov 9 – 13', time: null },
    ] as FollowUp[],
    careTeam: [
      { initials: 'TH', name: 'T. Haywood, MD', role: 'Supervising Clinician', bg: '#2D6670' },
      { initials: 'ER', name: 'Eva, RDN', role: 'Coach', bg: '#5299A9' },
    ],
    diagnoses: ['IBS-C', 'GERD'],
    membership: { type: 'Collaborative Care', since: 'January 2024', status: 'Active' },
    insurance: { primary: 'Blue Cross Blue Shield', memberId: '00123456' },

    // C_Sidebar/Patient — fields for the right rail
    nextVisit: {
      date: 'Nov 13, 3:00 PM E…',
      status: 'Confirmed' as const,
      type: 'Follow-up Visit',
      provider: 'T. Haywood, MD',
      quote: 'I would like help managing side effects of my meds. They’re giving me a stomach ache and …',
    },
    detailRows: [
      { label: 'Diagnoses', value: 'IBS-C*, GERD**, CD, UC' },
      { label: 'Symptoms', value: 'Bloating*, Diarrhea**, Reflux,', moreLabel: '3 more' },
      { label: 'Health System', value: 'Mayo Clinic' },
      { label: 'Referring', value: 'Dr. Montgomery' },
      { label: 'Reported at Onboarding', value: 'GI Surgery, Pregnant, Eating Disorder' },
    ],
    subscriptionAccount: [
      'useremail@gmail.com',
      'Pro, Stripe Subscription',
      'Blue Cross Blue Shield NM',
      'New Mexico (CST)',
    ],
    riskHistory: [
      { level: 'moderate' as const, date: '03/26/25' },
      { level: 'low' as const, date: '03/26/25' },
      { level: 'high' as const, date: '03/26/25' },
    ],
    meetingHistory: [
      { date: '4/15/2025', type: 'E&M', status: 'upcoming' as const },
      { date: '4/15/2025', type: 'PCM', status: 'billed' as const },
      { date: '4/15/2025', type: 'PCM', status: 'no-show' as const },
    ],
  },
};

export type CareOsPatientProfile = (typeof patientProfiles)[keyof typeof patientProfiles];

// ─── Claims Insights ────────────────────────────────────────────────────────
// Pre-initial-visit snapshot rendered INSIDE the Patient Summary tab when the
// member hasn't had a first visit yet (see PatientProfile). `no-claims-data`
// models a client whose feed provides nothing to compute from.
//
// Imported directly by PreVisitClaimsInsights (no MSW handler/hook) — this
// feature is prototype-only local state, same as journeyStage/activeLabOrder,
// per an explicit "mocked data and local state only, no new API surface" call.

// Standardized copy per insight type × severity (PRD "Semantic states" table,
// two-dimension revision). Verbatim from product — template copy, not
// personalized with a patient name.
const INSIGHT_COPY: Record<InsightType, Record<InsightSeverity, string>> = {
  'medication-management': {
    low: 'Appears to take few medications that need high clinical management',
    moderate: 'Appears to take some medications that need high-touch clinical management',
    high: 'Appears to take multiple medications that need high-touch clinical management',
  },
  'clinical-volatility': {
    low: 'Appears to be an average user of the healthcare system',
    moderate: 'Appears to be an above average user of the healthcare system',
    high: 'Recent acute clinical instability, including emergency department, urgent-care, and/or inpatient utilization',
  },
};

function buildInsight(id: string, type: InsightType, severity: InsightSeverity): ClaimsInsight {
  return { id, type, severity, copy: INSIGHT_COPY[type][severity] };
}

// Rows ordered high → low risk (PRD interaction note), which is also the
// order in the reference mock: volatility (high) above medication (moderate).
const claimsInsightsWithData: ClaimsInsightsData = {
  hasClaimsData: true,
  insights: [
    buildInsight('clinical-volatility', 'clinical-volatility', 'high'),
    buildInsight('medication-management', 'medication-management', 'moderate'),
  ],
};

const claimsInsightsNoData: ClaimsInsightsData = {
  hasClaimsData: false,
  insights: [],
};

export const claimsInsightsFixtures: Record<ClaimsInsightsState, ClaimsInsightsData> = {
  'before-first-visit': claimsInsightsWithData,
  'summary-ready': claimsInsightsWithData,
  'steady-state': claimsInsightsWithData,
  'no-claims-data': claimsInsightsNoData,
};
